import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Download, ShieldCheck, CalendarClock, AlertCircle, BookOpen } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api, { API } from "@/lib/api";
import usePageSeo from "@/hooks/usePageSeo";

const fmtBytes = (n) => {
  if (!n || n <= 0) return "—";
  const kb = n / 1024;
  return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};
const fmtDate = (s) => {
  if (!s) return "Not yet published";
  try { return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return s; }
};

export default function TrustCenter() {
  usePageSeo({
    title: "Trust Center — Dividend Portfolio Planner",
    description: "DPP policy docs: Terms of Service, Privacy Policy, Cookie Policy, Acceptable Use, and data-handling commitments.",
    path: "/trust",
    ogSlug: "trust",
  });

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDoc, setOpenDoc] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/trust/documents");
        const list = r.data.documents || [];
        setDocs(list);
        // Deep-link support: /trust?doc=affiliate-terms auto-opens the
        // reader for that policy (used by the /affiliate T&Cs link and
        // by the 30-day-notice email we send to affiliates).
        const wantSlug = searchParams.get("doc");
        if (wantSlug) {
          const target = list.find((d) => d.slug === wantSlug);
          if (target && (target.body_md || target.has_file)) {
            if (target.body_md) setOpenDoc(target);
            // if PDF-only, users already have a Download link — leave modal closed
          }
        }
      } catch (err) {
        toast.error("Could not load policy documents");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="mb-8">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" /> Trust Center
        </div>
        <h1 className="serif text-4xl mt-2">Policies &amp; documents</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">
          Every policy governing your use of Dividend Portfolio Planner lives here. Documents marked <strong>Required</strong> must be accepted at signup and again when a new version is published. Everything else is informational — download at any time.
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2, 3].map((i) => <div key={i} className="h-24 rounded-md bg-muted animate-pulse" />)}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="trust-documents">
          {docs.map((d) => (
            <Card key={d.slug} data-testid={`trust-doc-${d.slug}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="w-9 h-9 rounded-md bg-primary/10 grid place-items-center text-primary">
                    <FileText className="w-4 h-4" />
                  </div>
                  {d.required_for_signup && (
                    <Badge variant="outline" className="mono text-[9px] uppercase tracking-widest text-secondary border-secondary/40" data-testid={`trust-required-${d.slug}`}>
                      Required
                    </Badge>
                  )}
                </div>
                <div className="serif text-xl mt-3">{d.title}</div>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{d.description}</p>
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2 mt-4">
                  <CalendarClock className="w-3 h-3" />
                  {d.has_file ? (
                    <>v{d.current_version} · {fmtDate(d.current_updated_at)} · {fmtBytes(d.current_size_bytes)}</>
                  ) : d.body_md ? (
                    <>Inline · updated {fmtDate(d.current_updated_at)}</>
                  ) : (
                    <span className="text-muted-foreground">Not yet published</span>
                  )}
                </div>
                <div className="mt-4">
                  {d.has_file ? (
                    <a
                      href={`${API}/trust/documents/${d.slug}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors px-3.5 py-1.5 text-sm"
                      data-testid={`trust-download-${d.slug}`}
                    >
                      <Download className="w-3.5 h-3.5" /> Download PDF
                    </a>
                  ) : d.body_md ? (
                    <button
                      type="button"
                      onClick={() => setOpenDoc(d)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors px-3.5 py-1.5 text-sm"
                      data-testid={`trust-read-${d.slug}`}
                    >
                      <BookOpen className="w-3.5 h-3.5" /> Read policy
                    </button>
                  ) : (
                    <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground italic">
                      <AlertCircle className="w-3.5 h-3.5" /> Policy PDF coming soon
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="mt-10 rounded-md border border-border bg-muted/30 p-5 text-sm text-muted-foreground">
        <p className="leading-relaxed">
          Questions about these policies? <Link to="/contact" className="underline underline-offset-2 hover:text-primary">Contact us</Link> and we&apos;ll route your message to the right team.
        </p>
      </div>

      {/* Inline-body policy reader — renders Markdown docs (Affiliate T&Cs,
          DPA, and any other admin-authored dynamic policy) without needing a
          PDF upload. */}
      <Dialog open={!!openDoc} onOpenChange={(o) => !o && setOpenDoc(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="trust-body-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-2xl">{openDoc?.title}</DialogTitle>
            {openDoc?.description && (
              <DialogDescription>{openDoc.description}</DialogDescription>
            )}
          </DialogHeader>
          {openDoc?.body_md && (
            <div
              className="prose prose-sm max-w-none text-foreground"
              data-testid="trust-body-content"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(openDoc.body_md) }}
            />
          )}
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-4">
            Last updated {fmtDate(openDoc?.current_updated_at || openDoc?.created_at)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
